// Sample tutorials data
export const tutorialsData = [
  {
    id: 1,
    title: "How to build a clean landing page and a multi-step email automation in SendFox",
    slug: "sendfox-landingpage-automation",
    description: "Step-by-step guide to build a clean landing page and a multi-step email automation in SendFox.",
    tool: "Sendfox",
    category: "Marketing",
    duration: "5 min",
    difficulty: "Intermediate",
    videoId: "iJvOvLDsAj8",
    content: `## The Email Capture Problem

Tired of trying to duct-tape a landing page to an email sequence? Let's cut the crap. You need a simple way to capture emails and automatically send a welcome series. **No complex tools, no endless theory.** Just a straight path from signup to inbox.

This guide will show you exactly how to build a clean landing page and a multi-step email automation in SendFox.

## What You'll Build by the End

By the end of this tutorial, you will have a **fully functional email capture system**:

- A live SendFox landing page (also called a "Smart Page") to collect new subscribers
- An automated email sequence that triggers the moment someone signs up, delivering a series of welcome emails over several days

**Skill Level**: Beginner  
**Estimated Time**: 20-30 minutes

## Prerequisites and Tools

All you need is one thing:
- **A SendFox account** (the free plan is sufficient to get started)

## Step-by-Step Walkthrough

We're going to tackle this in two logical parts. First, we create the container for our contacts (the List). Then we build the page to get them on that list. Finally, we create the automation that talks to them.

### Part 1: Create Your List and Landing Page (Time: ~10 mins)

This is the foundation. **Don't build the page before you know where the subscribers will go.** It's a rookie mistake.

#### Step 1: Create an Email List
This is the most critical first step. You need a list to assign your landing page subscribers to.

1. From your SendFox dashboard, navigate to **Audience**
2. Make sure you're on the **Lists** tab
3. Click **Create List**
4. Give it a clear, specific name (e.g., "YT Test List" or "Newsletter Signups")
5. Click **Create**

That's it. You now have a home for your future contacts.

#### Step 2: Create the Landing Page (Smart Page)
Now we build the front door.

1. Navigate to **Growth** in the top menu
2. You'll see "Smart Pages." Click **Create Smart Page**
3. SendFox gives you two options: the "Smart Page Assistant" or starting fresh
4. For this guide, click **"I don't have a website"** to start with a clean template
5. You'll land on a default page. In the top left, click **Edit Smart Page**

#### Step 3: Customize Your Landing Page Content & Design

This is where you make the page your own. The editor is split into three tabs: **Content**, **Visual**, and **Form**.

**Content Tab**:
- **Title**: This is the SEO title that shows up in the browser tab. Make it compelling
- **Landing Page Editor**: Write your headline and body text. Be direct. What will people get for signing up?

**Visual Tab**:
- **Page Header**: Choose to display your name or upload a logo
- **Theme Color**: Pick a color for your buttons and links
- **Page Image** (Optional): Upload a background image

**Form Tab** (Most Important):
- **What information would you like to collect?**: Keep it simple. First Name and Email are usually enough
- **List**: Click the dropdown and select the list you created in Step 1
- **Button copy**: Change "Join" to something more actionable like "Get the Guide"
- **Require GDPR Consent**: If you have subscribers from the European Union, check this box
- **Redirect URL** (Optional): You can send people to a "thank you" page after they subscribe

Click **Save and Preview** in the top right corner. Your landing page is now live!

### Part 2: Build the Email Automation (Time: ~15 mins)

Now that we have a way to collect emails, let's build the machine that sends them messages automatically.

#### Step 1: Create a New Automation Series
1. Navigate to **Automations** in the top menu
2. Click **Create Automation Series**
3. SendFox offers a template assistant. This is the fastest way to start
4. Select **Template automation**
5. Fill in your details (name, website)
6. Select your goal (e.g., Welcome email series)
7. Give the automation a name (e.g., "YT Welcome Series")
8. Click **Continue**

#### Step 2: Set the Starting Trigger
This tells SendFox when to start the automation. **If you get this wrong, nothing will happen.**

1. The new automation will be empty with a prompt to "Add a starting trigger." Click it
2. In the "Select List" dropdown, choose the exact same list you connected to your landing page
3. Click **Save**

Now, anyone who joins that list will be entered into this automation.

#### Step 3: Customize the Email Sequence
The template will generate a few draft emails. Now you need to edit them.

**First Email (The Welcome Email)**:
- This should have "Deliver: No Delay." You must deliver the value you promised immediately
- Click **Edit Email**
- Write a clear **Subject** and compelling **Preview Text**
- In the body, welcome the new subscriber. If you promised a PDF or a link, provide it here
- Click **Save**

**Subsequent Emails**:
- You'll see delays like "Deliver: Wait for 1 day"
- Click **"Change delivery time"** to adjust this to hours, days, or weeks
- Click **Edit Email** for each step to write your content
- To add more emails, scroll to the bottom and click **Add new email**

Once you're done, make sure the toggle in the top right is set to **Running**.

**You're done.** You now have a complete system.

## Common Mistakes and How to Avoid Them

### Mistake: Building the landing page before the list
**How to Avoid**: Always create your Audience List first. It forces you to think about organization from the start.

### Mistake: Forgetting to set the automation trigger
**How to Avoid**: The very first thing you should do after creating an automation is set the trigger. Double-check that it's linked to the correct list.

### Mistake: The automation is built but not sending
**How to Avoid**: After you finish editing, ensure the automation status is set to **"Running."**

## Pro Tips from Real Experience

- **Test Your Funnel**: Use a spare email address and sign up through your own landing page. Did you get the welcome email?

- **Keep It Simple**: SendFox's strength is its simplicity. Don't overcomplicate it. A simple page and a 3-email welcome sequence are more effective than a complex system you never finish.

- **Use the {{contact.first_name}} Tag**: Personalize your emails by starting with **Hey {{contact.first_name}},**

## Next Steps and Advanced Techniques

1. **Promote Your Link**: Your landing page is useless if no one sees it. Add the link to your social media bios, email signature, and YouTube descriptions.

2. **Create Content-Specific Lead Magnets**: Instead of one generic landing page, create multiple. For example, a page offering a "Beginner's Guide to SEO" that triggers an SEO-focused email sequence.

3. **Segment Your Subscribers**: At the end of your welcome sequence, you can add an "Add email interaction" step to move the contact to your main "Newsletter" list.`
  },
  {
    id: 2,
    title: "Import your Calendly bookings to Tidycal in 60 seconds",
    slug: "import-calendly-to-tidycal",
    description: "Import your Calendly bookings to Tidycal in 60 seconds.",
    tool: "Tidycal",
    category: "Business",
    duration: "1 min",
    difficulty: "Beginner",
    videoId: "1RHdg-cngOA",
    content: `## The Migration Problem

Alright, let's get straight to it. You're switching from Calendly to TidyCal and the last thing you want to do is **manually rebuild every single booking type** you've already perfected. It's a waste of time. 

This guide will show you how to pull all your Calendly settings into TidyCal in about **90 seconds**. No fluff, just the steps.

## What You'll Achieve by the End

By the end of this guide, you will have a **fully connected TidyCal account** with all of your existing Calendly booking types imported and ready to use. You'll be able to ditch Calendly without losing any of your setup.

## Prerequisites & Tools

**Skill Level**: Beginner  
**Time Estimate**: 2 minutes

**Required Tools**:
- An active TidyCal account
- An active Calendly account with at least one booking type configured
- It helps to be logged into both accounts in the same web browser

## Step-by-Step Walkthrough: Importing from Calendly to TidyCal

Follow these steps exactly. **It's almost impossible to mess this up.**

### Step 1: Navigate to Integrations in TidyCal
First, log into your TidyCal dashboard. In the top-right corner, click on your profile icon (or the three lines) to open the menu. Select **Integrations**.

### Step 2: Find the Calendly Import Option
On the Integrations page, you'll see a menu on the left. Click on **Import from Calendly**. This is a dedicated feature, not a standard integration.

### Step 3: Connect Your Calendly Account
You'll see a big blue button that says **Connect Calendly account**. Click it. This will redirect you to an authorization screen.

Click **Connect to Calendly** on the pop-up window to give TidyCal permission to access your event types. If you aren't logged into Calendly, you'll be prompted to log in first.

### Step 4: Run the Import
Once you've authorized the connection, you'll be sent back to TidyCal. You will now see a new button: **Import booking types now**. Click it.

The system will work for a second and then it's done. You'll see a small confirmation at the bottom of the screen.

### Step 5: Verify the Imported Booking Types
Now, let's make sure it worked. Navigate to the **Booking Types** tab in the main TidyCal navigation. You should see your imported Calendly event right there. In the video example, the "Tidycall is better than Calendly" event was successfully imported.

**That's it. You're done.** All your meeting types are now in TidyCal.

## Common Mistakes and How to Avoid Them

### Mistake #1: Expecting a perfect 1-to-1 copy
The importer is great for names, durations, and basic settings. However, it might not pull over very specific or advanced Calendly settings. **Always double-check the settings** of each imported booking type in TidyCal before you send out the link.

### Mistake #2: Not clicking the final "Import" button
Just connecting the account isn't enough. People often connect it and then wonder why nothing happened. **You must click the "Import booking types now" button** after connecting.

### Mistake #3: Running the import multiple times
If you run the import again, it will likely create duplicates of your booking types. This is meant to be a **one-time migration tool**, not a continuous sync.

## Pro Tips from Real Experience

- **Clean up Calendly first**: Before you import, delete any old or unused booking types in Calendly. This prevents you from migrating junk into your new, clean TidyCal setup.

- **Update your links immediately**: Once you confirm the import worked, go to your email signature, your website's contact page, and anywhere else you have your Calendly link, and replace it with your new TidyCal link.

- **Disconnect the integration after you're done**: Once you've successfully migrated and are happy with the setup, you can go back into the TidyCal integrations and disconnect Calendly. It's good practice for security.

## Next Steps and Advanced Techniques

1. **Configure TidyCal-specific settings**: Now that your basic types are imported, explore TidyCal's features. Set up custom branding, connect your payment processors (Stripe), and explore the "Date Polls" feature.

2. **Review Calendar Connections**: Go to Integrations > Calendars in TidyCal and make sure the correct Google, Microsoft, or Apple calendars are connected for checking conflicts and adding new events.

## FAQ Section

**Q: Will this import my past or scheduled appointments from Calendly?**

A: No. This tool only imports your booking types (the event templates). It does not import your calendar history or any meetings that have already been scheduled.

**Q: What happens if the import fails or I get an error?**

A: The most common fix is to go back to TidyCal's integration settings, disconnect Calendly, and then try the connection process again from the start. Also, ensure you don't have any browser extensions interfering with the authorization pop-up.

**Q: Can I import from more than one Calendly account?**

A: No. You can only connect and import from one Calendly account at a time. To import from another, you would need to disconnect the first one.`
  },
  {
    id: 3,
    title: "ClickFunnels 2.0: Build High-Converting Funnels Fast",
    slug: "clickfunnels-funnel-builder",
    description: "Learn to build sales funnels that convert. From landing pages to checkout, master the complete ClickFunnels workflow for maximum results.",
    tool: "ClickFunnels",
    category: "Marketing",
    duration: "45 min",
    difficulty: "Intermediate",
    videoId: "9T-R8ugEg28",
    content: `# ClickFunnels 2.0: Build High-Converting Funnels Fast

Master the art of funnel building with ClickFunnels 2.0. This comprehensive guide takes you from beginner to funnel expert in **45 minutes**.

## What You'll Build

A complete sales funnel including:
- **High-converting landing page**
- **Lead magnet delivery system**  
- **Email sequence automation**
- **Sales page with payment processing**
- **Thank you page with upsells**

## Prerequisites

- ClickFunnels 2.0 account (14-day free trial available)
- Basic understanding of sales funnels
- Product or service to sell

## Module 1: Funnel Strategy (10 minutes)

Before building, **plan your funnel**:

### Define Your Customer Journey
1. **Awareness**: How do people find you?
2. **Interest**: What problem do you solve?
3. **Desire**: Why should they choose you?
4. **Action**: What's the first step?

### Choose Your Funnel Type
- **Lead Generation**: Capture emails with free offer
- **Sales Funnel**: Direct product sales
- **Webinar Funnel**: Educational content to sales
- **Application Funnel**: Qualify leads before sales call

## Module 2: Building Your Landing Page (15 minutes)

### Step 1: Choose Your Template
1. Navigate to **Funnels** → **Create New Funnel**
2. Select **"Lead Generation Funnel"**
3. Choose a proven template (avoid starting from scratch)
4. Click **"Build Funnel"**

### Step 2: Customize Your Headline
Your headline determines success:
- Focus on the **benefit**, not the feature
- Use **specific numbers** when possible
- Create **urgency or scarcity**
- Test different variations

**Example Headlines**:
- "Download the 7-Step System That Generated $100K in 90 Days"
- "Get the Exact Email Templates That Convert 23% Better"

### Step 3: Optimize Your Form
- Keep it simple: **Name and email only**
- Use **action-oriented button text**
- Place form **above the fold**
- Add **trust indicators** (testimonials, logos)

### Step 4: Add Social Proof
- Customer testimonials with photos
- Number of customers served
- Media mentions or awards
- Before/after results

## Module 3: Email Integration & Automation (10 minutes)

### Connect Your Email Service
1. Go to **Account Settings** → **Integrations**
2. Connect your email provider (ActiveCampaign, Mailchimp, etc.)
3. Map form fields to email list
4. **Test the connection**

### Set Up Your Email Sequence
Create a **5-email welcome series**:
1. **Email 1**: Deliver lead magnet + introduce yourself
2. **Email 2**: Share your story/why you created this
3. **Email 3**: Case study or success story
4. **Email 4**: Address common objections
5. **Email 5**: Soft pitch for your main offer

## Module 4: Sales Page Creation (15 minutes)

### Page Structure That Converts
1. **Attention-grabbing headline**
2. **Problem identification**
3. **Solution presentation**
4. **Benefits over features**
5. **Social proof section**
6. **Objection handling**
7. **Strong call-to-action**
8. **Risk reversal** (guarantee)

### Copy Framework: PAS
- **Problem**: What keeps your customer awake at night?
- **Agitation**: Make the problem hurt more
- **Solution**: Present your offer as the answer

### Design Best Practices
- Use plenty of **white space**
- Stick to **2-3 colors maximum**  
- Make **buttons stand out**
- Use **bullet points** for easy scanning
- Add **progress indicators** for long pages

## Module 5: Payment & Thank You Pages (10 minutes)

### Payment Page Optimization
- Display **security badges**
- Show **order summary clearly**
- Offer **multiple payment options**
- Keep **form fields minimal**
- Add **testimonials near checkout**

### Thank You Page Strategy
Don't waste this high-engagement moment:
- **Confirm their purchase**
- **Set expectations** for delivery
- Offer **complementary upsell**
- Encourage **social sharing**
- **Collect feedback**

## Module 6: Testing & Optimization (5 minutes)

### Essential Tests
1. **Mobile responsiveness**: 60%+ traffic is mobile
2. **Page load speed**: Under 3 seconds
3. **Form functionality**: Test submissions
4. **Payment processing**: Complete test purchase
5. **Email delivery**: Check all autoresponders

### A/B Testing Ideas
- Headlines and subheadlines
- Button colors and text
- Form placement
- Pricing presentation
- Social proof positioning

## Advanced Funnel Strategies

### Tripwire Funnels
- **Low-cost offer** ($7-$27) after lead magnet
- Converts leads to buyers
- Covers advertising costs
- Increases lifetime value

### Webinar Funnels
- Registration page with high-value promise
- Reminder email sequence
- Live or automated presentation
- Limited-time offer at the end

### Application Funnels
- Qualify leads before sales calls
- Higher close rates on calls
- Better use of sales team time
- Premium positioning

## Common Mistakes to Avoid

1. **Too many form fields**: Keep it simple
2. **Weak headlines**: Test multiple versions
3. **No mobile optimization**: Most traffic is mobile
4. **Ignoring page speed**: Slow pages kill conversions
5. **No follow-up sequence**: The money is in the follow-up

## Funnel Analytics That Matter

Track these key metrics:
- **Traffic sources**: Where are visitors coming from?
- **Conversion rates**: What percentage opt-in/buy?
- **Cost per lead**: How much to acquire each lead?
- **Customer lifetime value**: Revenue per customer
- **Return on ad spend**: Profit from advertising

## Next Steps

1. **Launch your first funnel** with basic optimization
2. **Drive traffic** through paid ads or organic content
3. **Collect data** for at least 100 visitors per variation
4. **Test one element at a time**
5. **Scale what works**, fix what doesn't

Remember: **A mediocre funnel with traffic beats a perfect funnel with no visitors.** Launch fast, optimize continuously.

Your funnel is now ready to convert visitors into customers. Focus on driving quality traffic and let the system do the work.`
  },
  {
    id: 4,
    title: "Supabase Setup: Build a Full-Stack App in 20 Minutes",
    slug: "supabase-fullstack-setup",
    description: "Complete Supabase tutorial for beginners. Set up authentication, database, and real-time features to build modern web applications.",
    tool: "Supabase",
    category: "Development",
    duration: "20 min",
    difficulty: "Beginner",
    videoId: "5aDKUFMca-s",
    content: `# Supabase Setup: Build a Full-Stack App in 20 Minutes

Learn to build a complete full-stack application with Supabase. This tutorial covers **authentication, database operations, and real-time features**.

## What You'll Build

A task management app with:
- **User authentication** (signup/login)
- **Task creation and management**
- **Real-time updates**
- **Row-level security**
- **File uploads**

## Prerequisites

- Basic JavaScript/React knowledge
- Node.js installed
- Text editor (VS Code recommended)

## Getting Started

### Create Supabase Project
1. Go to **supabase.com** and sign up
2. Click **"New Project"**
3. Choose organization and project name
4. Wait for database setup (2-3 minutes)
5. **Note your project URL and anon key**

### Set Up Local Environment
Create a new React app and install Supabase:

\`\`\`bash
npx create-react-app supabase-tasks
cd supabase-tasks
npm install @supabase/supabase-js
\`\`\`

## Database Schema Design

### Create Tasks Table
In your Supabase dashboard:

1. Go to **Table Editor**
2. Click **"Create a new table"**
3. Name it **"tasks"**
4. Add columns:
   - **id** (int8, primary key)
   - **title** (text, required)
   - **description** (text)
   - **completed** (bool, default false)
   - **user_id** (uuid, references auth.users)
   - **created_at** (timestamptz, default now())

### Enable Row Level Security
Run these SQL commands:

\`\`\`sql
-- Enable RLS
ALTER TABLE tasks ENABLE ROW LEVEL SECURITY;

-- Users can only see their own tasks
CREATE POLICY "Users can view own tasks" ON tasks
  FOR SELECT USING (auth.uid() = user_id);

-- Users can insert their own tasks
CREATE POLICY "Users can insert own tasks" ON tasks
  FOR INSERT WITH CHECK (auth.uid() = user_id);

-- Users can update their own tasks
CREATE POLICY "Users can update own tasks" ON tasks
  FOR UPDATE USING (auth.uid() = user_id);

-- Users can delete their own tasks
CREATE POLICY "Users can delete own tasks" ON tasks
  FOR DELETE USING (auth.uid() = user_id);
\`\`\`

## Client Setup

### Configure Supabase Client
Create **src/supabaseClient.js**:

\`\`\`javascript
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = 'YOUR_SUPABASE_URL'
const supabaseKey = 'YOUR_SUPABASE_ANON_KEY'

export const supabase = createClient(supabaseUrl, supabaseKey)
\`\`\`

### Authentication Component
Create **src/Auth.js** with login and signup functionality. The component will handle user authentication with **email and password**.

## Task Management Features

### Tasks Component
Create **src/Tasks.js** to handle:
- **Fetching user tasks**
- **Adding new tasks**
- **Toggling task completion**
- **Deleting tasks**
- **Real-time subscriptions**

## Main App Component

Update **src/App.js** to:
- **Check for existing session**
- **Listen for auth state changes**
- **Render Auth or Tasks component** based on session

## Advanced Features

### File Uploads
Add avatar upload functionality using **Supabase Storage**.

### Real-time Subscriptions
Listen to database changes and **update UI automatically**.

## Deployment

### Environment Variables
Create **.env.local** with your Supabase credentials:
\`\`\`
REACT_APP_SUPABASE_URL=your-project-url
REACT_APP_SUPABASE_ANON_KEY=your-anon-key
\`\`\`

### Deploy to Vercel
\`\`\`bash
npm run build
npx vercel --prod
\`\`\`

## Security Best Practices

1. **Always use Row Level Security**
2. **Validate data** on the client and server
3. **Use environment variables** for keys
4. **Implement proper error handling**
5. **Test your policies thoroughly**

## Next Steps

- Add **task categories and tags**
- Implement **task sharing between users**
- Add **email notifications**
- Build a **mobile app with React Native**
- Explore **Supabase Edge Functions**

You now have a **fully functional full-stack application** with authentication, real-time features, and secure data access. The power of Supabase lets you build complex applications with minimal backend code.`
  }
];